import { useCallback, useEffect, useRef, useState } from "react"
import { usePoll } from "@inertiajs/react"

import { postJSON } from "@/lib/http"
import { useTakeoffStore } from "@/stores/takeoff-store"
import type { LockInfo } from "@/types/takeoff"

type ClaimResponse = { ok: boolean; token: string | null; lock: LockInfo }

const HEARTBEAT_MS = 30_000

/**
 * One-editor lock lifecycle. Claims on mount; while holding, heartbeats every
 * 30s; on claim failure (or a lost heartbeat) flips the store read-only and
 * polls lock/document/savepoints so the viewer tracks the editor near-live.
 * The token never leaves this hook except through `lockTokenRef` — it is not
 * store state and never appears in props.
 */
export function useTakeoffLock(takeoffId: number) {
  const [editing, setEditing] = useState(false)
  const lockTokenRef = useRef<string | null>(null)

  const claim = useCallback(async () => {
    try {
      const { status, data } = await postJSON<ClaimResponse>(
        `/takeoffs/${takeoffId}/claim_lock`,
        {}
      )
      if (status === 200 && data?.ok && data.token) {
        lockTokenRef.current = data.token
        useTakeoffStore.getState().setReadOnly(false)
        setEditing(true)
      } else {
        lockTokenRef.current = null
        useTakeoffStore.getState().setReadOnly(true)
        setEditing(false)
      }
    } catch {
      // Network hiccup: stay (or become) read-only; the user can retry via
      // the banner button.
      lockTokenRef.current = null
      useTakeoffStore.getState().setReadOnly(true)
      setEditing(false)
    }
  }, [takeoffId])

  const lockLost = useCallback(() => {
    lockTokenRef.current = null
    useTakeoffStore.getState().setReadOnly(true)
    setEditing(false)
  }, [])

  // Claim once on mount. (Release happens through the autosave hook's final
  // flush, so a normal close both saves and frees the lock atomically.)
  useEffect(() => {
    claim()
  }, [claim])

  // Heartbeat while editing; a failed beat means another tab/user took over.
  useEffect(() => {
    if (!editing) return
    const interval = setInterval(async () => {
      const token = lockTokenRef.current
      if (!token) return
      try {
        const { status } = await postJSON(`/takeoffs/${takeoffId}/heartbeat`, {}, {
          headers: { "X-Takeoff-Lock-Token": token },
        })
        if (status !== 200) lockLost()
      } catch {
        // Transient network failure — keep the lock; the next beat retries
        // well inside the 90s staleness window.
      }
    }, HEARTBEAT_MS)
    return () => clearInterval(interval)
  }, [editing, takeoffId, lockLost])

  // Read-only viewers poll for lock changes and document updates.
  const { start, stop } = usePoll(
    2000,
    { only: ["lock", "document", "savepoints"] },
    { autoStart: false }
  )
  useEffect(() => {
    if (editing) stop()
    else start()
    return () => stop()
  }, [editing, start, stop])

  return { editing, claim, lockLost, lockTokenRef }
}
